// Efectos de sonido generados programáticamente con Web Audio API

class SoundEffects {
  private audioContext: AudioContext | null = null;
  private muted: boolean = false;

  constructor() {
    this.initAudioContext();
    const saved = localStorage.getItem('gameSoundMuted');
    this.muted = saved === 'true';
  }

  public setMuted(muted: boolean) {
    this.muted = muted;
    localStorage.setItem('gameSoundMuted', String(muted));
  }

  public toggleMute(): boolean {
    this.setMuted(!this.muted);
    return this.muted;
  }

  public getMuted(): boolean {
    return this.muted;
  }

  private initAudioContext() {
    try {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    } catch (e) {
      console.warn('Audio context no disponible:', e);
    }
  }

  private async ensureAudioContext() {
    if (!this.audioContext) this.initAudioContext();
    if (this.audioContext && this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }
  }

  // ✅ ACIERTO COMPLETO — Campanilla alegre (Do Mi Sol Do)
  async playSuccessSound() {
    if (this.muted) return;
    await this.ensureAudioContext();
    if (!this.audioContext || this.audioContext.state !== 'running') return;
    try {
      const frequencies = [523.25, 659.25, 783.99, 1046.5];
      const startTime = this.audioContext.currentTime;
      frequencies.forEach((freq, i) => {
        const osc = this.audioContext!.createOscillator();
        const gain = this.audioContext!.createGain();
        osc.connect(gain);
        gain.connect(this.audioContext!.destination);
        osc.frequency.setValueAtTime(freq, startTime + i * 0.1);
        gain.gain.setValueAtTime(0, startTime + i * 0.1);
        gain.gain.linearRampToValueAtTime(0.3, startTime + i * 0.1 + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.8);
        osc.start(startTime + i * 0.1);
        osc.stop(startTime + 0.85);
      });
    } catch (e) {
      console.warn('Error en playSuccessSound:', e);
    }
  }

  // 🟠 ACIERTO PARCIAL — Ascendente incompleto (Mi Sol Si)
  async playPartialSuccessSound() {
    if (this.muted) return;
    await this.ensureAudioContext();
    if (!this.audioContext || this.audioContext.state !== 'running') return;
    try {
      const notes = [329.63, 392.00, 493.88];
      for (let i = 0; i < notes.length; i++) {
        const osc = this.audioContext.createOscillator();
        const gain = this.audioContext.createGain();
        osc.connect(gain);
        gain.connect(this.audioContext.destination);
        const t = this.audioContext.currentTime + i * 0.25;
        osc.frequency.setValueAtTime(notes[i], t);
        gain.gain.setValueAtTime(0, t);
        gain.gain.linearRampToValueAtTime(0.3, t + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, t + 0.4);
        osc.start(t);
        osc.stop(t + 0.45);
      }
    } catch (e) {
      console.warn('Error en playPartialSuccessSound:', e);
    }
  }

  // ❌ ERROR — Game Over descendente (Do La Fa# Re Si)
  async playErrorSound() {
    if (this.muted) return;
    await this.ensureAudioContext();
    if (!this.audioContext || this.audioContext.state !== 'running') return;
    try {
      const notes = [523.25, 440, 369.99, 293.66, 246.94];
      for (let i = 0; i < notes.length; i++) {
        const osc = this.audioContext.createOscillator();
        const gain = this.audioContext.createGain();
        osc.connect(gain);
        gain.connect(this.audioContext.destination);
        const t = this.audioContext.currentTime + i * 0.12;
        osc.frequency.setValueAtTime(notes[i], t);
        gain.gain.setValueAtTime(0, t);
        gain.gain.linearRampToValueAtTime(0.3, t + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, t + 0.25);
        osc.start(t);
        osc.stop(t + 0.3);
      }
    } catch (e) {
      console.warn('Error en playErrorSound:', e);
    }
  }

  // ⬆️ LEVEL UP — Variante 35 seleccionada
  async playLevelUpSound() {
    if (this.muted) return;
    await this.ensureAudioContext();
    if (!this.audioContext || this.audioContext.state !== 'running') return;
    try {
      const notes = [261.63, 329.63, 392.0, 523.25, 659.25, 587.33, 659.25, 783.99, 1046.5];
      for (let i = 0; i < notes.length; i++) {
        const osc = this.audioContext.createOscillator();
        const gain = this.audioContext.createGain();
        osc.connect(gain);
        gain.connect(this.audioContext.destination);
        const t = this.audioContext.currentTime + i * 0.095;
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(notes[i], t);
        gain.gain.setValueAtTime(0, t);
        gain.gain.linearRampToValueAtTime(0.28, t + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, t + 0.42);
        osc.start(t);
        osc.stop(t + 0.45);
      }
    } catch (e) {
      console.warn('Error en playLevelUpSound:', e);
    }
  }

  // Llamar una vez tras la primera interacción del usuario para desbloquear el contexto
  async activateAudio() {
    await this.ensureAudioContext();
    if (!this.audioContext) return;
    try {
      const osc = this.audioContext.createOscillator();
      const gain = this.audioContext.createGain();
      osc.connect(gain);
      gain.connect(this.audioContext.destination);
      gain.gain.setValueAtTime(0.001, this.audioContext.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.0001, this.audioContext.currentTime + 0.01);
      osc.start(this.audioContext.currentTime);
      osc.stop(this.audioContext.currentTime + 0.01);
    } catch (e) {}
  }
}

export const soundEffects = new SoundEffects();
export const playSuccessSound = () => soundEffects.playSuccessSound();
export const playPartialSuccessSound = () => soundEffects.playPartialSuccessSound();
export const playErrorSound = () => soundEffects.playErrorSound();
export const playLevelUpSound = () => soundEffects.playLevelUpSound();
export const activateAudio = () => soundEffects.activateAudio();
export const toggleMute = () => soundEffects.toggleMute();
export const setMuted = (v: boolean) => soundEffects.setMuted(v);
export const isMuted = () => soundEffects.getMuted();
